// ─────────────────────────────────────────────────────────────────────
// imageProxy — Auto-proxy via wsrv.nl pour les CDN partenaires bloqués
// ─────────────────────────────────────────────────────────────────────
//
// Certains CDN partenaires bloquent React Native (CORS, anti-hotlinking,
// throttling, ou simplement très lents depuis le mobile) :
//   - static.zara.net      → bloque les requêtes sans Referer correct
//   - static.tous.com      → très lent + parfois bloqué
//   - cashmereboutique.com → CORS strict
//
// Solution : on les fait passer par le proxy d'images public wsrv.nl
// qui télécharge l'image, la convertit en JPEG, et la sert avec les bons
// CORS headers. Bonus : il redimensionne (`&w=800`) ce qui accélère.
//
// v1.10.91 : centralisation des règles. Avant, dupliquées dans 3 composants
// (ProductDetailModal, OutfitCard) et MANQUANTES dans WardrobeScreen, ce qui
// causait des images TOUS qui ne s'affichent pas dans le dressing.

const PROXIED_DOMAINS = [
  'static.zara.net',
  'static.tous.com',
  'cashmereboutique.com',
];

/**
 * Si l'URL pointe sur un CDN partenaire bloqué/lent, retourne l'URL passant
 * par le proxy wsrv.nl. Sinon, retourne l'URL telle quelle.
 *
 * @param {string} url - URL d'image originale (peut être null/undefined)
 * @param {object} opts
 *   - width  : largeur cible en px (défaut 800)
 *   - force  : forcer le proxy même pour les domaines non listés
 * @returns {string|null} URL proxifiée ou originale
 */
export function getProxiedImageUrl(url, opts = {}) {
  if (!url || typeof url !== 'string') return url;

  // v1.12.902 — Force HTTPS sur Android (cleartext bloqué) + sur web (Mixed Content).
  // Beaucoup d'anciens items dressing ont des URLs http:// stockées en DB.
  if (url.startsWith('http://')) {
    url = 'https://' + url.slice(7);
  }

  // Déjà passée par le proxy ? Ne pas re-wrapper
  if (url.includes('wsrv.nl')) return url;
  if (url.includes('/img-proxy')) return url;

  // S3 / data URI / file URI / HTTP local → passer direct
  if (url.startsWith('data:')) return url;
  if (url.startsWith('file:')) return url;
  if (url.startsWith('content:')) return url;
  if (url.includes('amazonaws.com')) return url;

  const width = opts.width || 800;

  // Vérifier si l'URL contient un domaine connu pour bloquer/ralentir
  const needsProxy = opts.force === true
    || PROXIED_DOMAINS.some(domain => url.includes(domain));

  if (!needsProxy) return url;

  return `https://wsrv.nl/?url=${encodeURIComponent(url)}&w=${width}`;
}

/**
 * Détecte si une URL pointe sur un domaine connu pour bloquer.
 * Utile pour décider si on doit forcer le proxy en cas de fallback.
 */
export function isBlockedDomain(url) {
  if (!url || typeof url !== 'string') return false;
  return PROXIED_DOMAINS.some(domain => url.includes(domain));
}
